import APP_CONFIG from '@/config/app';
import {
  TokenInterface,
  UnderlyingYieldBearingAssetInterface,
} from '@/interfaces';
import { TransactionInterface } from '@/interfaces/TransactionInterface';
import {
  formatNumberToAmountInTokenCurrencyString,
  formatNumberToAmountInUsdString,
  formatToSentenceCase,
} from '@/utils/formatUtils';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import React, { ReactNode } from 'react';
import { IconBaseProps } from 'react-icons';
import {
  FaArrowDownLong,
  FaArrowRightArrowLeft,
  FaArrowUpLong,
} from 'react-icons/fa6';
import { GiGrowth } from 'react-icons/gi';
import DepositIntoYieldBearingAssetIcon from '../../public/images/png/deposit-into-investment.png';
import DepositIntoWalletIcon from '../../public/images/png/deposit-into-wallet.png';
import TradeIcon from '../../public/images/png/trade.png';
import WithdrawalFromWalletIcon from '../../public/images/png/withdrawal-from-wallet.png';
dayjs.extend(isBetween);
export const getWalletAddressFromTransaction = ({
  transaction,
  walletAddresses,
}: {
  transaction: TransactionInterface;
  walletAddresses: string[];
}): string => {
  if (
    transaction.type === 'wallet-deposit' &&
    transaction.sendingWalletAddress
  ) {
    return transaction.sendingWalletAddress;
  } else if (
    transaction.type === 'wallet-withdrawal' &&
    transaction.receivingWalletAddress
  ) {
    return transaction.receivingWalletAddress;
  }
  return 'Unknown';
};
export const getLast6OfWalletAddressFromTransaction = ({
  transaction,
  walletAddresses,
}: {
  transaction: TransactionInterface;
  walletAddresses: string[];
}): string => {
  if (
    transaction.type === 'wallet-deposit' &&
    transaction.sendingWalletAddress
  ) {
    return transaction.sendingWalletAddress.slice(-6);
  }
  return 'Unknown';
};
export const getShortenedWalletAddressFromTransaction = ({
  transaction,
  walletAddresses,
  prefix = '',
}: {
  transaction: TransactionInterface;
  walletAddresses: string[];
  prefix?: string;
}): string => {
  if (
    transaction.type === 'wallet-deposit' &&
    transaction.sendingWalletAddress
  ) {
    const address = transaction.sendingWalletAddress;
    return `${prefix}${address.slice(0, 4)}...${address.slice(-4)}`;
  }
  return 'Unknown';
};
export const getTransactionTypeDetails = ({
  transaction,
  walletAddresses,
  showAmountInUsd = false,
}: {
  transaction: TransactionInterface;
  walletAddresses: string[];
  showAmountInUsd?: boolean;
}): {
  mainText: string;
  subText: string | undefined;
  iconPng: string | undefined;
  icon: ReactNode | undefined;
} => {
  let mainText = 'Unknown Transaction';
  let subText: string | undefined;
  let iconPng: string | undefined;
  let icon: ReactNode | undefined;
  if (showAmountInUsd && transaction.amountInUsd) {
    subText = `Balance ${formatNumberToAmountInUsdString(
      parseFloat(transaction.amountInUsd),
    )}`;
  }
  switch (transaction.type) {
    case 'wallet-deposit':
      if (transaction.sendingWalletAddress) {
        subText = `Sending wallet ${transaction.sendingWalletAddress.slice(
          0,
          4,
        )}...${transaction.sendingWalletAddress.slice(-4)}`;
      }
      mainText = 'Deposit into wallet';
      iconPng = DepositIntoWalletIcon.src;
      icon = React.createElement(
        FaArrowDownLong as React.ComponentType<IconBaseProps>,
        {
          className: 'text-secondary',
        },
      );
      break;
    case 'wallet-withdrawal':
      if (transaction.receivingWalletAddress) {
        subText = `Receiving wallet ${transaction.receivingWalletAddress.slice(
          0,
          4,
        )}...${transaction.receivingWalletAddress.slice(-4)}`;
      }
      mainText = 'Withdrawal from wallet';
      icon = WithdrawalFromWalletIcon.src;
      icon = React.createElement(
        FaArrowUpLong as React.ComponentType<IconBaseProps>,
        {
          className: 'text-secondary',
        },
      );
      break;
    case 'trade':
      mainText = `Bought ${transaction.buyAsset?.name} (${transaction.buyAsset?.symbol})`;
      subText = `Sold ${transaction.sellAsset?.name} (${transaction.sellAsset?.symbol})`;
      iconPng = TradeIcon.src;
      icon = React.createElement(
        FaArrowRightArrowLeft as React.ComponentType<IconBaseProps>,
        {
          className: 'text-secondary',
        },
      );
      break;
    case 'yield-bearing-asset-deposit':
      mainText = transaction.yieldBearingAsset?.ticker
        ? `Earn with ${
            transaction.yieldBearingAsset?.name ??
            transaction.yieldBearingAsset.ticker
          }${
            transaction.yieldBearingAsset?.name
              ? ` (${transaction.yieldBearingAsset.ticker})`
              : ''
          }`
        : 'Earn';
      subText = `Deployed ${transaction.asset?.name} (${transaction.asset?.symbol})`;
      iconPng =
        transaction.yieldBearingAsset?.iconUrl ||
        DepositIntoYieldBearingAssetIcon.src;
      icon = React.createElement(
        GiGrowth as React.ComponentType<IconBaseProps>,
        {
          className: 'text-secondary',
        },
      );
      break;
    default:
      break;
  }
  return { mainText, subText, iconPng, icon };
};
export const getDirectionAdjustedAmountInNativeCurrencyFromTransaction = ({
  transaction,
}: {
  transaction: TransactionInterface;
}): number => {
  const amountValue = parseFloat(transaction.amount);
  return transaction.type === 'wallet-withdrawal' ? -amountValue : amountValue;
};
export const getDirectionAdjustedAmountInUSDFromTransaction = ({
  transaction,
}: {
  transaction: TransactionInterface;
}): number => {
  const amountValue = transaction.amountInUsd
    ? parseFloat(transaction.amountInUsd)
    : 0;
  return transaction.type === 'wallet-withdrawal' ? -amountValue : amountValue;
};
export const getDirectionAdjustedFeeAmountInUSDFromTransaction = ({
  transaction,
}: {
  transaction: TransactionInterface;
}): number => {
  const feeValue = transaction.networkFeeInUsd
    ? parseFloat(transaction.networkFeeInUsd)
    : 0;
  return transaction.type === 'wallet-withdrawal' ? -feeValue : feeValue;
};
export const generateLineChartData = (
  initialAmount: number,
  apy: number,
  years: number,
  round: boolean = false,
): { value: number; time: string; label: string }[] => {
  const data = [];
  const currentYear = new Date().getFullYear();
  for (let i = 0; i <= years; i++) {
    const time = currentYear + i;
    const factor = Math.pow(1 + apy / 100, i);
    let value = initialAmount * factor;
    if (round) {
      value = parseFloat(value.toFixed(2));
    }
    data.push({
      value,
      time: time.toString(),
      label: formatNumberToAmountInUsdString(value, round),
    });
  }
  return data;
};
export function generateTokenId<T extends Partial<TokenInterface>>(
  token: T,
): T & { id: string } {
  if (
    (token as any).id &&
    (token as any).id !== '' &&
    (token as any).id !== 'chain-ethereum-addr-0x0'
  ) {
    return { ...token, id: (token as any).id };
  }
  let tokenId = `chain-${token.blockchain?.toLowerCase()}-addr-${token.address?.toLowerCase()}`;
  if (token.address?.toLowerCase() === '0x0') {
    if ((token as any).yieldBearingNativeAssetId) {
      tokenId += `-${(token as any).yieldBearingNativeAssetId}`;
    } else if ((token as any).defiLlamaId) {
      tokenId += `-${(token as any).defiLlamaId}`;
    } else if ((token as any).name) {
      const nameSlug = (token as any).name
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-');
      tokenId += `-${nameSlug}`;
    }
  }
  return { ...token, id: tokenId };
}
export const getTokenLogoSrc = (
  ticker: string,
): { tokenLogoSrc: string; defaultLogoSrc: string } => {
  return {
    tokenLogoSrc: `/images/crypto-logos/svg/color/${ticker.toLowerCase()}.svg`,
    defaultLogoSrc: '/images/crypto-logos/svg/color/generic.svg',
  };
};
export const convertTokenAmountToUsd = (
  tokenAmount: number,
  tokenData: TokenInterface,
): number => {
  if (tokenData.priceInUsd) {
    return tokenAmount * tokenData.priceInUsd;
  }
  return 0;
};
export const areAllocationsDifferent = (
  originalAllocation: any[],
  currentAllocation: any[],
): boolean => {
  if (!currentAllocation || currentAllocation.length === 0) {
    return false;
  }
  if (originalAllocation.length !== currentAllocation.length) {
    return true;
  }
  return originalAllocation.some((original, index) => {
    const current = currentAllocation[index];
    return (
      original.underlyingYieldBearingAssetId !==
        current.underlyingYieldBearingAssetId ||
      original.composition !== current.composition
    );
  });
};
export const replaceTokensInString = (
  label: string,
  replacements: Record<string, string>,
): string => {
  return label.replace(/\[(\w+)\]/g, (matched, key) => {
    return replacements[key] || matched;
  });
};
export const getTokenImageSrc = (ticker: string): string => {
  const tokenSymbolLowercase = ticker.toLowerCase();
  const imagePaths = APP_CONFIG.TOKEN_IMAGE_PATHS(tokenSymbolLowercase);
  return imagePaths[0];
};
export const getCommonDates = (
  dataMap: Map<string, any[]>,
  dateKey: string,
): string[] => {
  const allDatesArrays = Array.from(dataMap.values()).map((dataArray) =>
    dataArray.map((item) => item[dateKey]?.split('T')[0]),
  );
  if (allDatesArrays.length === 0) return [];
  let commonDates = allDatesArrays[0];
  for (let i = 1; i < allDatesArrays.length; i++) {
    commonDates = commonDates.filter((date) =>
      allDatesArrays[i].includes(date),
    );
  }
  return commonDates;
};
export const generateWhatThisMeansMessage = (
  endingAmountUSD: number,
  startingAmountUSD: number,
  tokenAmount: number,
  tokenSymbol: string,
  totalYears: number,
  totalReturnPercentage: number,
): string => {
  return `You are projected to earn ${formatNumberToAmountInUsdString(
    endingAmountUSD,
  )} based on your initial investment of ${formatNumberToAmountInTokenCurrencyString(
    tokenAmount,
    tokenSymbol,
  )} (${formatNumberToAmountInUsdString(
    startingAmountUSD,
  )}) over ${totalYears} years for a total return of ${totalReturnPercentage.toFixed(
    2,
  )}%.`;
};
export const generateEthereumNetworkDescription = ({
  showEthereumOnly = true,
}: { showEthereumOnly?: boolean } = {}): string => {
  const { SUPPORTED_BLOCKCHAIN_IDS } = APP_CONFIG.WALLET;
  const subNetworks = SUPPORTED_BLOCKCHAIN_IDS.filter(
    (blockchain) => blockchain.toLowerCase() !== 'ethereum',
  ).map(formatToSentenceCase);
  if (showEthereumOnly || subNetworks.length === 0) {
    return 'the Ethereum blockchain network.';
  }
  if (subNetworks.length === 1) {
    return `the Ethereum or its sub-network, ${subNetworks[0]} blockchain network.`;
  }
  return `the Ethereum or one of its sub-networks, ${subNetworks
    .slice(0, -1)
    .join(', ')}, or ${
    subNetworks[subNetworks.length - 1]
  } blockchain networks.`;
};
export const generateLogoUri = (
  symbol: string,
  existingLogoUri?: string,
): string => {
  if (existingLogoUri) {
    return existingLogoUri;
  }
  const symbolLowercase = symbol.toLowerCase();
  const imagePaths = APP_CONFIG.TOKEN_IMAGE_PATHS(symbolLowercase);
  return imagePaths[0];
};
export const calculateEffectiveApy = (
  allocations: Array<{
    apy: number;
    tvl: number;
    composition: number;
  }>,
): number => {
  const totalComposition = allocations.reduce(
    (acc, allocation) => acc + allocation.composition,
    0,
  );
  if (totalComposition === 0) {
    console.warn('Total composition is 0. Returning effective APY as 0.');
    return 0;
  }
  const effectiveApy = allocations.reduce((acc, allocation) => {
    return acc + (allocation.apy || 0) * (allocation.composition || 0);
  }, 0);
  return effectiveApy;
};
export const aggregateAllocations = (
  timeSeriesData: Array<{
    timestamp: string;
    tvlUsd: number;
    apy: number;
  }>,
  composition: number,
  monthsToShow?: number,
): Array<{
  date: string;
  tvl: number;
  apy: number;
  composition: number;
}> => {
  const currentDate = new Date();
  return timeSeriesData
    .filter((dataPoint) => {
      if (!monthsToShow) return true;
      const dataDate = new Date(dataPoint.timestamp);
      return (
        currentDate.getTime() - dataDate.getTime() <=
        monthsToShow * 30 * 24 * 60 * 60 * 1000
      );
    })
    .map((dataPoint) => ({
      date: dataPoint.timestamp.split('T')[0],
      tvl: dataPoint.tvlUsd || 0,
      apy: dataPoint.apy || 0,
      composition,
    }));
};
export const calculateAggregatedTimeSeriesYieldData = (
  assetsYieldData: Array<{ id: string; timeSeriesData: any[] }>,
  compositions: Array<{ composition: number }>,
  monthsToShow: number = APP_CONFIG.HISTORICAL_YIELD_DATA_TO_SHOW_IN_MONTHS,
): any[] => {
  const allDataPoints: Map<
    string,
    {
      tvl: number;
      allocations: Array<{ apy: number; tvl: number; composition: number }>;
    }
  > = new Map();
  const currentDate = new Date();
  assetsYieldData.forEach(({ timeSeriesData }, assetIndex) => {
    const compositionWeight = compositions[assetIndex].composition;
    const allocations = aggregateAllocations(timeSeriesData, compositionWeight);
    allocations.forEach(({ date, tvl, apy, composition }) => {
      const dataDate = new Date(date);
      if (
        monthsToShow &&
        currentDate.getTime() - dataDate.getTime() >
          monthsToShow * 30 * 24 * 60 * 60 * 1000
      ) {
        return;
      }
      if (!allDataPoints.has(date)) {
        allDataPoints.set(date, {
          tvl: 0,
          allocations: [],
        });
      }
      const currentData = allDataPoints.get(date)!;
      currentData.tvl += tvl;
      currentData.allocations.push({ apy, tvl, composition });
    });
  });
  return Array.from(allDataPoints.entries())
    .sort(
      ([dateA], [dateB]) =>
        new Date(dateA).getTime() - new Date(dateB).getTime(),
    )
    .map(([date, data]) => ({
      timestamp: date,
      tvlUsd: data.tvl,
      apy: calculateEffectiveApy(data.allocations),
    }));
};
export const generateProjectionData = (
  amountInUsd: number,
  apy: number,
): Array<{ value: number; time: string; label: string }> => {
  return generateLineChartData(amountInUsd, apy, 12, true);
};
export const generateEffectiveProjectionData = (
  allocations: Array<{
    apy: number;
    tvl: number;
    composition: number;
  }>,
  totalAmountInUsd: number,
): Array<{ value: number; time: string; label: string }> => {
  const effectiveApy = calculateEffectiveApy(allocations);
  return generateLineChartData(totalAmountInUsd, effectiveApy, 12, true);
};
export const calculateProjectionSummary = (
  amount: number,
  usdPrice: number,
  projectionData: Array<{ value: number; time: string; label: string }>,
): {
  startingAmountUSD: number;
  endingAmountUSD: number;
  totalReturnPercentage: number;
} => {
  const startingAmountUSD = amount * usdPrice;
  const endingAmountUSD = projectionData.length
    ? projectionData[projectionData.length - 1].value
    : 0;
  const totalReturnPercentage =
    startingAmountUSD > 0
      ? ((endingAmountUSD - startingAmountUSD) / startingAmountUSD) * 100
      : 0;
  return {
    startingAmountUSD,
    endingAmountUSD,
    totalReturnPercentage,
  };
};
export const calculateProjectionData = (
  assets: UnderlyingYieldBearingAssetInterface[],
  totalAmountInUsd: number,
  monthsToShow: number = 12,
): {
  projectionData: Array<{ value: number; time: string; label: string }>;
  effectiveApy: number;
} => {
  const allocations = assets.map((asset) => ({
    apy: asset.apy || 0,
    tvl: asset.tvlUSD || 0,
    composition: asset.composition || 0,
  }));
  const effectiveApy = calculateEffectiveApy(allocations);
  const projectionData = generateEffectiveProjectionData(
    allocations,
    totalAmountInUsd,
  );
  return { projectionData, effectiveApy };
};
export const preAggregateData = (
  dailyData: any[],
  interval: 'weekly' | 'monthly',
): any[] => {
  const dataPointsByInterval: Map<
    string,
    { tvlUsd: number; apy: number; count: number }
  > = new Map();
  dailyData.forEach((dataPoint) => {
    let intervalKey: string;
    if (interval === 'weekly') {
      intervalKey = dayjs(dataPoint.timestamp)
        .startOf('week')
        .format('YYYY-MM-DD');
    } else {
      intervalKey = dayjs(dataPoint.timestamp)
        .startOf('month')
        .format('YYYY-MM-DD');
    }
    if (!dataPointsByInterval.has(intervalKey)) {
      dataPointsByInterval.set(intervalKey, { tvlUsd: 0, apy: 0, count: 0 });
    }
    const currentData = dataPointsByInterval.get(intervalKey)!;
    currentData.tvlUsd += dataPoint.tvlUsd;
    currentData.apy += dataPoint.apy;
    currentData.count++;
  });
  return Array.from(dataPointsByInterval.entries()).map(([date, data]) => ({
    timestamp: date,
    tvlUsd: data.tvlUsd,
    apy: data.apy / data.count,
  }));
};
export const aggregateYieldDataForAssetsByTimeInterval = (
  assetsYieldData: Array<{ id: string; timeSeriesData: any[] }>,
  compositions: Array<{ composition: number }>,
  aggregationTimeInterval: 'daily' | 'weekly' | 'monthly',
): any[] => {
  const allDataPoints: Map<string, { tvl: number; allocations: any[] }> =
    new Map();
  assetsYieldData.forEach(({ timeSeriesData }, assetIndex) => {
    const compositionWeight = compositions[assetIndex].composition;
    timeSeriesData.forEach((dataPoint) => {
      const date = dayjs(dataPoint.timestamp).format('YYYY-MM-DD');
      let aggregatedDate = date;
      if (aggregationTimeInterval === 'weekly') {
        aggregatedDate = dayjs(dataPoint.timestamp)
          .startOf('week')
          .format('YYYY-MM-DD');
      } else if (aggregationTimeInterval === 'monthly') {
        aggregatedDate = dayjs(dataPoint.timestamp)
          .startOf('month')
          .format('YYYY-MM-DD');
      }
      if (!allDataPoints.has(aggregatedDate)) {
        allDataPoints.set(aggregatedDate, { tvl: 0, allocations: [] });
      }
      const currentData = allDataPoints.get(aggregatedDate)!;
      currentData.tvl += dataPoint.tvlUsd * compositionWeight;
      currentData.allocations.push({
        apy: dataPoint.apy * compositionWeight,
        tvl: dataPoint.tvlUsd * compositionWeight,
        composition: compositionWeight,
      });
    });
  });
  return Array.from(allDataPoints.entries()).map(([date, data]) => ({
    timestamp: date,
    tvlUsd: data.tvl,
    apy:
      data.allocations.reduce((sum, allocation) => sum + allocation.apy, 0) /
      data.allocations.length,
  }));
};
export function calculateOptimalPortfolioForRiskFromMonthlyApyAndTvlData(
  assets: {
    timeSeriesData: { timestamp: string; apy: number; tvlUsd: number }[];
  }[],
  riskTolerance: number,
): number[] {
  const setsOfTimestamps = assets.map(
    (a) => new Map(a.timeSeriesData.map((d) => [d.timestamp, d])),
  );
  const commonTimestamps = setsOfTimestamps.reduce((acc, currentMap) => {
    if (acc.size === 0) {
      currentMap.forEach((_v, key) => acc.add(key));
    } else {
      for (const ts of [...acc]) {
        if (!currentMap.has(ts)) acc.delete(ts);
      }
    }
    return acc;
  }, new Set<string>());
  const timestamps = [...commonTimestamps].sort();
  if (!timestamps.length) {
    return assets.map(() => 1 / assets.length);
  }
  const monthlyReturnsByAsset: number[][] = [];
  const tvlByAsset: number[][] = [];
  for (let i = 0; i < assets.length; i++) {
    const dataMap = new Map(
      assets[i].timeSeriesData.map((d) => [d.timestamp, d]),
    );
    const monthlyReturns: number[] = [];
    const tvls: number[] = [];
    for (const ts of timestamps) {
      const entry = dataMap.get(ts);
      if (!entry) {
        continue;
      }
      monthlyReturns.push((entry.apy ?? 0) / 100 / 12);
      tvls.push(entry.tvlUsd ?? 0);
    }
    monthlyReturnsByAsset.push(monthlyReturns);
    tvlByAsset.push(tvls);
  }
  const weightedAverageReturns = monthlyReturnsByAsset.map(
    (returnsArr, assetIdx) => {
      let weightedSum = 0;
      let totalWeight = 0;
      for (let t = 0; t < returnsArr.length; t++) {
        weightedSum += returnsArr[t] * tvlByAsset[assetIdx][t];
        totalWeight += tvlByAsset[assetIdx][t];
      }
      return totalWeight === 0 ? 0 : weightedSum / totalWeight;
    },
  );
  const n = assets.length;
  const covMatrix: number[][] = Array.from({ length: n }, () =>
    Array(n).fill(0),
  );
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      let numerator = 0;
      let totalWeight = 0;
      for (let t = 0; t < monthlyReturnsByAsset[i].length; t++) {
        const weight = tvlByAsset[i][t];
        const diffI = monthlyReturnsByAsset[i][t] - weightedAverageReturns[i];
        const diffJ = monthlyReturnsByAsset[j][t] - weightedAverageReturns[j];
        numerator += weight * diffI * diffJ;
        totalWeight += weight;
      }
      covMatrix[i][j] = totalWeight === 0 ? 0 : numerator / totalWeight;
    }
  }
  const A = covMatrix.map((row) => row.slice());
  const identity: number[][] = [];
  for (let i = 0; i < n; i++) {
    identity[i] = [];
    for (let j = 0; j < n; j++) {
      identity[i][j] = i === j ? 1 : 0;
    }
  }
  for (let i = 0; i < n; i++) {
    let pivot = A[i][i];
    if (pivot === 0) {
      let swapRow = i + 1;
      while (swapRow < n && A[swapRow][i] === 0) swapRow++;
      if (swapRow === n) {
        return Array(n).fill(1 / n);
      }
      [A[i], A[swapRow]] = [A[swapRow], A[i]];
      [identity[i], identity[swapRow]] = [identity[swapRow], identity[i]];
      pivot = A[i][i];
    }
    for (let col = 0; col < n; col++) {
      A[i][col] /= pivot;
      identity[i][col] /= pivot;
    }
    for (let row = 0; row < n; row++) {
      if (row !== i) {
        const factor = A[row][i];
        for (let col = 0; col < n; col++) {
          A[row][col] -= factor * A[i][col];
          identity[row][col] -= factor * identity[i][col];
        }
      }
    }
  }
  const invCov = identity;
  const onesVector = Array(n).fill(1);
  const product: number[] = [];
  for (let i = 0; i < n; i++) {
    let rowSum = 0;
    for (let j = 0; j < n; j++) {
      rowSum += invCov[i][j] * onesVector[j];
    }
    product.push(rowSum);
  }
  const sumProduct = product.reduce((a, b) => a + b, 0);
  let minVarWeights: number[];
  if (sumProduct === 0) {
    minVarWeights = Array(n).fill(1 / n);
  } else {
    minVarWeights = product.map((x) => x / sumProduct);
  }
  minVarWeights = minVarWeights.map((w) => Math.max(w, 0));
  const sumWeights = minVarWeights.reduce((a, b) => a + b, 0);
  if (sumWeights === 0) {
    minVarWeights = Array(n).fill(1 / n);
  } else {
    minVarWeights = minVarWeights.map((w) => w / sumWeights);
  }
  let bestIndex = 0;
  for (let i = 1; i < weightedAverageReturns.length; i++) {
    if (weightedAverageReturns[i] > weightedAverageReturns[bestIndex]) {
      bestIndex = i;
    }
  }
  const maxRetWeights = Array(n).fill(0);
  maxRetWeights[bestIndex] = 1;
  const blendedWeights = minVarWeights.map((wMin, i) => {
    const wMax = maxRetWeights[i];
    return (1 - riskTolerance) * wMin + riskTolerance * wMax;
  });
  let total = blendedWeights.reduce((a, b) => a + b, 0);
  if (total === 0) {
    return Array(n).fill(1 / n);
  }
  let normalized = blendedWeights.map((w) => Math.max(w, 0) / total);
  const finalSum = normalized.reduce((a, b) => a + b, 0);
  if (finalSum === 0) {
    return Array(n).fill(1 / n);
  }
  return normalized.map((w) => w / finalSum);
}
export const calculateAverageYield = (
  timeSeriesData: { timestamp: string; apy: number }[],
  period: 'ytd' | 30 | 60 | 90,
): number | null => {
  if (!timeSeriesData || timeSeriesData.length === 0) {
    return null;
  }
  const sortedData = [...timeSeriesData].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );
  const latestDate = dayjs(sortedData[sortedData.length - 1].timestamp);
  let startDate;
  let daysInPeriod;
  if (period === 'ytd') {
    startDate = latestDate.startOf('year');
    daysInPeriod = latestDate.diff(startDate, 'day');
  } else {
    startDate = latestDate.subtract(period, 'day');
    daysInPeriod = period;
  }
  const relevantData = sortedData.filter((d) =>
    dayjs(d.timestamp).isAfter(startDate),
  );
  if (period !== 'ytd' && relevantData.length < daysInPeriod * 0.75) {
    return null;
  }
  if (period === 'ytd' && relevantData.length === 0) {
    return null;
  }
  return relevantData.reduce((sum, d) => sum + d.apy, 0) / relevantData.length;
};
